import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../components/auth/AuthContext'
import { useOnboardingResume } from '../hooks/useOnboarding'
import { LoginForm } from '../components/auth/LoginForm'
import { LoadingScreen } from '../components/ui/LoadingScreen'
import toast from 'react-hot-toast'

interface LoginFormValues {
  email: string
  password: string
}

export const LoginPage = () => {
  const { login, isAuthenticated, isLoading, isLoggingIn, mustChangePassword } =
    useAuth()
  const { handleResumeRedirect } = useOnboardingResume()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const from = location.state?.from?.pathname || '/dashboard'

  // Redirigir si ya está autenticado y no está cargando
  useEffect(() => {
    // login() todavía está decidiendo y ejecutando su propia navegación
    // (pending-proposals, progressURL, create-password) — esperar a que
    // termine para no competir con ella y terminar mostrando /dashboard
    // por un instante antes del destino real.
    if (isLoggingIn) return

    if (mustChangePassword && !isLoading) {
      navigate('/create-password', { replace: true })
      return
    }
    if (isAuthenticated && !isLoading) {
      // Verificar si necesita onboarding antes de redirigir
      handleResumeRedirect().catch(() => {
        // En caso de error, ir al destino original
        navigate(from, { replace: true })
      })
    }
    // IMPORTANT: Do not call handleResumeRedirect when not authenticated
    // This prevents 401 errors that cause redirect loops
  }, [
    isAuthenticated,
    isLoading,
    isLoggingIn,
    mustChangePassword,
    navigate,
    from,
    handleResumeRedirect,
  ])

  const handleSubmit = async (values: LoginFormValues) => {
    if (isSubmitting) return // Prevenir doble submit

    setErrorMessage(undefined)
    setIsSubmitting(true)

    try {
      await login(values.email, values.password)
      toast.success('¡Bienvenido!')
      // La redirección se maneja en el useEffect con verificación de onboarding
    } catch (error: any) {
      console.error('Error en login:', error)

      const errorMsg = error.message || 'Error al iniciar sesión'

      // Mapear errores específicos para el componente
      if (
        errorMsg.includes('Usuario no encontrado') ||
        errorMsg.includes('not found')
      ) {
        setErrorMessage('email_not_found')
      } else if (
        errorMsg.includes('Credenciales incorrectas') ||
        errorMsg.includes('incorrecta')
      ) {
        setErrorMessage('wrong_password')
      } else {
        setErrorMessage('general_error')
        toast.error(errorMsg)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // Mostrar loading mientras se inicializa la autenticación
  if (isLoading) {
    return <LoadingScreen message="Verificando sesión..." />
  }

  // Si ya está autenticado, mostrar loading mientras se redirige
  if (isAuthenticated) {
    return <LoadingScreen message="Redirigiendo..." />
  }

  return <LoginForm onSubmit={handleSubmit} errorMessage={errorMessage} />
}

export default LoginPage
